/**
 * studentLiveSessionsListViewModel — pure `(reduxState) → props` de « Tous mes lives » de l'élève.
 *
 * Patron A, comme le parent : la liste est celle de `liveSessionsListViewModel` (sans e-mail
 * d'enfant : `getAllMyNextLiveSessions`). L'élève y ajoute ce que « Messages » portait avant que
 * ses prochains lives ne le quittent (Wajih, 18/10) : la garde de son « GO ! » (les occurrences qui
 * le comptent, `StudentLiveCardComponent.answerGo`) et « Planifier un rattrapage »
 * (`attendance_flags_to_be_recovered`, servi par la même lecture).
 *
 * Mémoïsé (`createSelector`) sur les tranches lues ; la liste, elle, l'est déjà par son propre VM.
 */
import { createSelector } from "reselect"

import { type ApiLiveOccurrence } from "../../../adapters/parent/LiveSessions/liveSessionListAdapter"
import {
    enrolledOccurrenceIds,
    recoveryCounts,
    type ApiFlagToBeRecovered,
} from "../../../adapters/student/Tutoring/studentTutoringAdapter"
import {
    liveSessionsListViewModel,
    type LiveSessionsListViewModel,
} from "../../parent/LiveSessions/liveSessionsListViewModel"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type StudentLiveSessionsListViewModel = LiveSessionsListViewModel & {
    /** Les occurrences où l'élève est inscrit : la garde de son « GO ! ». */
    enrolledSessionIds: string[]
    /** Au moins une absence reste à rattraper : « Planifier un rattrapage » est offert. */
    hasAbsencesToRecover: boolean
    recoverableNowCount: number
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

export const studentLiveSessionsListViewModel = createSelector(
    [
        (state: ReduxState) => liveSessionsListViewModel(state, null),
        (state: ReduxState) => state?.data?.liveSessions?.live_occurrences as ApiLiveOccurrence[] | undefined,
        (state: ReduxState) =>
            state?.data?.liveSessions?.attendance_flags_to_be_recovered as ApiFlagToBeRecovered[] | undefined,
        (state: ReduxState) => selectDisplayedStudent(state)?.id as string | number | undefined,
    ],
    (list, occurrences, flags, userId): StudentLiveSessionsListViewModel => {
        const { toRecover, recoverableNow } = recoveryCounts(flags)
        return {
            ...list,
            enrolledSessionIds: enrolledOccurrenceIds(occurrences, userId),
            hasAbsencesToRecover: toRecover > 0,
            recoverableNowCount: recoverableNow,
        }
    }
)
